import { getCookie, deleteCookie } from "@/js/utils/CookieUtil";
import { removeLocalItem } from '@/js/utils/StorageUtil';
import store from '@/js/store'; // Vuex store를 가져옴
import axios from "axios";
import GmxGlobal from "@/js/global";

import router from '@/router';

axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*";
axios.defaults.withCredentials = true;

//****Back-end JwtInterceptor와 일치해야 할 것.
const RES_CODE = {
    CLEAR : '1001', // 토큰이 만료된 경우
    JWT_NOTEQ : '1002', // 기타 JWT 검증 실패 시
    HEADER_NONE : '1003',// 헤더 정보가 없는 경우
    REFRESH : '1000',// Access 재발급.
    UNAUTHORIZED: 401
}

let refreshSubscribers = [];
let isRefreshing = false;

// 인증이 필요 없는 URL 패턴들
const AUTH_EXEMPT_URLS = [
    '/login',
    '/loginKey', 
    '/plainLogin',
    '/getAccessToken',
    '/join',
    '/find',
    'selectMtOrgzDesc',
    'insertMtUsrDesc'
];

// URL이 인증 예외 대상인지 확인
const isAuthExemptUrl = (url) => {
    return AUTH_EXEMPT_URLS.some(exemptUrl => url.includes(exemptUrl));
};

// 로그아웃 및 로그인 페이지 리다이렉트 처리
const handleAuthenticationFailure = (errorMessage = '인증이 만료되었습니다.') => {
    console.log('Authentication failed:', errorMessage);
    
    // 현재 페이지가 이미 로그인 페이지인 경우 리다이렉트 하지 않음
    const currentPath = window.location.pathname;
    if (currentPath === GmxGlobal.LOGIN_PAGE) {
        console.log('Already on login page, skipping redirect');
        return;
    }
    
    // 토큰 및 사용자 정보 모두 제거
    store.dispatch('systemInfo/clearToken');
    removeLocalItem('eUserId');
    removeLocalItem('eUserNm');
    removeLocalItem('searchVal');
    deleteCookie('access_token');
    deleteCookie('refresh_token');
    
    // 현재 페이지 정보 저장 (로그인 후 돌아오기 위해)
    const currentSearch = window.location.search;
    const linkChk = new URLSearchParams(currentSearch).get('link');
    
    // 로그인 페이지로 리다이렉트
    if (linkChk) {
        const param = new URLSearchParams(currentSearch).get('id');
        router.push({ 
            path: GmxGlobal.LOGIN_PAGE, 
            query: { 
                redirect: currentPath,
                link: '1',
                id: param 
            }
        });
    } else {
        router.push({ 
            path: GmxGlobal.LOGIN_PAGE,
            query: { redirect: currentPath }
        });
    }
};

// 사용자 토큰 유효성 검사
const isValidUser = () => {
    const token = store.getters['systemInfo/getToken'];
    const cookieToken = getCookie('access_token');
    
    console.log('🔍 isValidUser check - Store Token:', !!token, 'Cookie Token:', !!cookieToken);
    
    // 토큰이 있어야만 유효한 사용자로 간주 (사용자 ID만으로는 불충분)
    const isValid = (token && token.trim() !== '') || (cookieToken && cookieToken.trim() !== '');
    
    console.log('✅ User validation result:', isValid);
    return isValid;
};

// axios 인스턴스 생성
const axiosInstance = axios.create({
    headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
    },
    withCredentials: true,
    timeout: 30000, // 30초 타임아웃 설정 (5초 → 30초로 증가)
});

// JWT 토큰을 위한 설정1
axiosInstance.interceptors.request.use(
    async function (config) {
        console.log('🔍 Request interceptor - URL:', config.url);
        
        // 인증이 필요 없는 URL인 경우 토큰 검사 건너뛰기
        if (isAuthExemptUrl(config.url)) {
            console.log('✅ Auth exempt URL, skipping token check');
            return config;
        }
        
        // 현재 페이지가 로그인 페이지인 경우 토큰 검사 건너뛰기
        if (window.location.pathname === GmxGlobal.LOGIN_PAGE) {
            console.log('✅ Login page, skipping token check');
            return config;
        }
        
        // Store에서 Access Token 가져오기
        const token = store.getters['systemInfo/getToken'];
        const userId = store.getters['systemInfo/getUserId'];
        
        // 디버깅: 쿠키에서 직접 토큰도 확인
        const cookieToken = getCookie('access_token');
        console.log('🔑 Token check - Store token:', !!token, 'Cookie token:', !!cookieToken, 'UserId:', !!userId);
        
        // 토큰이 없지만 쿠키에 있다면 사용자 데이터 복원 시도
        if (!token && cookieToken) {
            console.log('🔄 No token in store but found in cookies, attempting full restore...');
            await store.dispatch('systemInfo/restoreUserData');
            // 복원 후 다시 토큰 가져오기
            const restoredToken = store.getters['systemInfo/getToken'];
            if (restoredToken) {
                console.log('✅ User data successfully restored from cookies');
                try {
                    const decodedToken = decodeURIComponent(restoredToken);
                    config.headers['Authorization'] = `Bearer=${decodedToken}`;
                    console.log('✅ Restored token added to request header');
                } catch (error) {
                    console.error('❌ Restored token decoding failed:', error);
                    config.headers['Authorization'] = `Bearer=${restoredToken}`;
                }
                return config;
            }
        }
        
        // 최종 토큰 확인 - 토큰이 없으면 로그인으로 리다이렉트
        const finalToken = store.getters['systemInfo/getToken'];
        if (!finalToken || finalToken.trim() === '') {
            console.warn('❌ No valid token available - redirecting to login');
            handleAuthenticationFailure('인증 토큰이 없습니다. 다시 로그인해주세요.');
            return Promise.reject(new Error('No valid token'));
        }
        
        // 토큰이 있으면 헤더에 추가
        try {
            const decodedToken = decodeURIComponent(finalToken);
            config.headers['Authorization'] = `Bearer=${decodedToken}`;
            console.log('✅ Token added to request header');
        } catch (error) {
            console.error('❌ Token decoding failed:', error);
            // 디코딩 실패해도 원본 토큰으로 시도
            config.headers['Authorization'] = `Bearer=${finalToken}`;
        }
        
        return config;
    },
    function (error) {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// JWT 토큰을 위한 설정2
axiosInstance.interceptors.response.use(
    (response) => {
        //console.log(response);
        console.log('✅ Response success for:', response.config.url);

        const isRefreshed = response.headers['token-refreshed'];
        const newAccessToken = response.headers['new-access-token'];

        if( isRefreshed && newAccessToken && newAccessToken != '' ) {
            store.dispatch('systemInfo/setToken', encodeURIComponent(newAccessToken));
        }
        return response;
    },
    (error) => {
        const { config, response } = error;
        
        console.log('❌ Response error for:', config?.url);
        
        // 응답이 없는 경우 (네트워크 오류 등)
        if (!response) {
            console.error('🌐 Network error or server not responding:', error.message);
            return Promise.reject(error);
        }
        
        const { status, headers } = response;
        const originalRequest = config;
        const GmxStatus = headers['gmx-status'];
        
        console.log('📊 Response status:', status, 'GMX Status:', GmxStatus);
        
        // 401 Unauthorized 처리
        if (status === RES_CODE.UNAUTHORIZED) {
            console.warn('🚫 Unauthorized access detected');
            handleAuthenticationFailure('인증 권한이 없습니다.');
            return Promise.reject(error);
        }
        
        // JWT 토큰 관련 오류 처리
        //alert(GmxStatus);
        if (GmxStatus) {
          //  alert(GmxStatus);
            switch (GmxStatus) {
                case RES_CODE.CLEAR:
                    console.warn('⏰ Token expired (CLEAR)');
                    handleAuthenticationFailure('토큰이 만료되었습니다.');
                    return Promise.reject(error);
                    
                case RES_CODE.JWT_NOTEQ:
                    console.warn('🔐 JWT verification failed');
                    handleAuthenticationFailure('JWT 검증에 실패했습니다.');
                    return Promise.reject(error);
                    
                case RES_CODE.HEADER_NONE:
                    console.warn('📋 Authorization header missing');
                    handleAuthenticationFailure('인증 헤더가 없습니다.');
                    return Promise.reject(error);
                    
                case RES_CODE.REFRESH:
                    console.log('🔄 Token refresh required');
                    // 토큰 갱신 처리
                    if (!isRefreshing) {
                        isRefreshing = true;
                        console.log('🚀 Starting token refresh process...');
                        
                        // const refreshToken = getCookie('refresh_token');
                        // if (!refreshToken) {
                        //     console.warn('❌ Refresh token not found in cookies');
                        //     handleAuthenticationFailure('리프레시 토큰이 없습니다.');
                        //     return Promise.reject(error);
                        // }
                        
                        // refresh token 디코딩
                     //   const decodedRefreshToken = decodeURIComponent(refreshToken);
                     //   console.log('🔑 Attempting token refresh with refresh token...');
                        
                        return axios.post("/esupport-api/getAccessToken", {}, {
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            timeout: 30000 // 토큰 갱신 요청도 30초 타임아웃 (5초 → 30초로 증가)
                        }).then(response => {
                            // 신규 Access Token 발급
                            const newToken = response.data.result?.accessToken;
                            
                            if (!newToken) {
                                console.error('❌ New token not received from server');
                                throw new Error('New token not received');
                            }
                            
                            console.log('✅ Token refresh successful, updating store...');
                            
                            // 새 토큰을 인코딩하여 저장
                            store.dispatch('systemInfo/setToken', encodeURIComponent(newToken));
                            
                            isRefreshing = false;
                            onRefreshed(newToken);
                            refreshSubscribers = [];
                            
                            console.log('🔄 Retrying original request with new token...');
                            // 원래 요청 재시도 (디코딩된 토큰 사용)
                            originalRequest.headers['Authorization'] = `Bearer=${newToken}`;
                            return axiosInstance(originalRequest);
                            
                        }).catch(refreshError => {
                            isRefreshing = false;
                            refreshSubscribers = [];
                            
                            console.error('❌ Token refresh failed:', refreshError);
                            handleAuthenticationFailure('토큰 갱신에 실패했습니다.');
                            return Promise.reject(refreshError);
                        });
                    }
                    
                    // 이미 갱신 중인 경우 대기열에 추가
                    console.log('⏳ Adding request to refresh queue (refresh in progress)');
                    return new Promise((resolve) => {
                        addRefreshSubscriber(token => {
                            console.log('🔄 Processing queued request with refreshed token');
                            originalRequest.headers['Authorization'] = `Bearer=${token}`;
                            resolve(axiosInstance(originalRequest));
                        });
                    });
                    
                default:
                    console.warn('❓ Unknown GMX status:', GmxStatus);
                    break;
            }
        }

        console.log('❌ Unhandled error, rejecting promise');
        return Promise.reject(error);
    }
);

const onRefreshed = token => {
    refreshSubscribers.map(callback => callback(token));
}

const addRefreshSubscriber = callback => {
    refreshSubscribers.push(callback);
}

// 기존 Request 함수 수정
const load = (url, opt, _callBack) => {
    // 인증이 필요 없는 URL인 경우 토큰 검사 건너뛰기
    if (!isAuthExemptUrl(url)) {
        // 현재 페이지가 로그인 페이지인 경우 토큰 검사 건너뛰기
        if (window.location.pathname !== GmxGlobal.LOGIN_PAGE) {
            // 요청 전 사용자 유효성 재검사
            if (!isValidUser()) {
                console.warn('Invalid user detected in load function');
                handleAuthenticationFailure('사용자 정보가 유효하지 않습니다.');
                _callBack({
                    data: {
                        status: 'error',
                        message: '사용자 인증이 필요합니다.'
                    }
                });
                return;
            }
        }
    }

    const prop = {
        method: opt.method || "get",
        url: url,
        headers: opt.header || { 
            "Access-Control-Allow-Origin": "*", 
            "Content-Type": "application/json", 
            'Authorization': `Bearer=${decodeURIComponent(store.getters['systemInfo/getToken'] || '')}` 
        },
        params: opt.param,
        responseType: "json",
        withCredentials: true,
        timeout: 30000, // 30초 타임아웃 설정 (5초 → 30초로 증가)
    };

    // 인증이 필요 없는 URL이거나 로그인 페이지인 경우 Authorization 헤더 제거
    if ((isAuthExemptUrl(url) || window.location.pathname === GmxGlobal.LOGIN_PAGE) && prop.headers && prop.headers['Authorization']) {
        delete prop.headers['Authorization'];
    }

    if (prop.method !== 'get') {
        prop['data'] = opt.param;
        delete prop.params;
    }

    axiosInstance(prop)
        .then(function (response) {
            _callBack(response);
        })
        .catch(function (error) {
            console.log("API request error:", error);
            _callBack(error);
        });
};

export default {
    load,
    handleAuthenticationFailure,
    isValidUser
};